import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigationType, useOutlet } from 'react-router-dom';
import { composeCacheKey, extractScreenKey, parseCacheKey, generateScreenKey, getScreenKeyPrefix } from './screenKeyUtils';
import { useFrameTimeMonitor } from '@/presentation/performanceObserver';
import { useTabVisibility } from '@/presentation/utils/tabVisibility';
import { MAIN_NAVIGATION_ROUTES } from './routes.config';

/**
 * RouteStack - Advanced Route Caching with Screen Key Support
 *
 * ARCHITECTURE OVERVIEW:
 * ======================
 * This file manages NAVIGATION screen keys for route caching ONLY.
 * Page components manage their own QUERY/VIDEO screen keys independently.
 *
 * ┌─────────────────────────────────────────────────────────────┐
 * │ SEPARATION OF CONCERNS                                      │
 * ├─────────────────────────────────────────────────────────────┤
 * │                                                             │
 * │ 1. Navigation Screen Keys (RouteStack)                     │
 * │    - Purpose: Route caching & component tree preservation  │
 * │    - Lifecycle: Changes on PUSH (detail routes only)       │
 * │    - Generated by: RouteStack.tsx (always)                 │
 * │    - Format: pathname + screenKey                          │
 * │    - Example: "/users/123#user-details-a3f9k2x7"          │
 * │                                                             │
 * │ 2. Query/Video Screen Keys (Page Components)               │
 * │    - Purpose: React Query cache & video state management   │
 * │    - Lifecycle: Stable per component instance              │
 * │    - Generated by: Each page component using refs          │
 * │    - Format: [queryScreenKey, 'queryType', ...params]     │
 * │    - Example: ['user-details-query-x1y2z3', 'userDetails']│
 * │                                                             │
 * └─────────────────────────────────────────────────────────────┘
 *
 * WHY SEPARATE KEYS?
 * ==================
 * Navigation keys need to change on every PUSH (for detail routes) to differentiate
 * route instances, but query/video keys need to remain stable for the component's lifetime.
 * Using the same key for both purposes caused infinite render loops.
 *
 * CACHE KEY STRATEGY:
 * ===================
 * Cache keys are composed of: pathname + screenKey
 * - Example: "/users/123#user-details-a3f9k2x7" or "/#feed-abc123"
 *
 * Main Navigation Routes (/, /inbox, /more) with persistCache: true:
 * - PUSH: Reuse existing screen key (singleton behavior)
 * - POP: Cache preserved, screen key unchanged
 * - Result: Single cached instance per route, never refreshes
 *
 * Detail Routes (/users/:id, /users/:id/videos) with persistCache: false:
 * - PUSH: Generate NEW screen key → Fresh navigation instance
 * - POP: Cache removed, React element unmounted from DOM
 * - Forward after POP: Generate NEW screen key → Fresh data
 *
 * DUAL CACHE SYSTEM:
 * ==================
 * 1. RouteStack Cache (this file):
 *    - What: React component trees (JSX elements)
 *    - Key: pathname + navigationScreenKey
 *    - Purpose: Preserve component state, scroll position
 *    - Mechanism: Cached in cacheRef.current, rendered with display: none/block
 *    - Cleanup: Immediately on POP (both cache state AND DOM removal)
 *
 * 2. React Query Cache (managed by page components):
 *    - What: API response data (JSON)
 *    - Key: [queryScreenKey, 'queryType', ...params]
 *    - Purpose: Avoid redundant API calls per component instance
 *    - Mechanism: In-memory cache with automatic GC
 *    - Cleanup: On component dispose (onDispose lifecycle)
 *
 * EXAMPLE DATA FLOW:
 * ==================
 *
 * SCENARIO A: Main Navigation (Feed ↔ Inbox - persistCache: true)
 * ----------------------------------------------------------------
 * 1. Initial load - Feed (PUSH):
 *    Navigation screenKey: 'feed-abc123' (generated once)
 *    Route cache key: '/#feed-abc123'
 *    FeedPage mounts, fetches data
 *
 * 2. Click Inbox in TopBar (PUSH):
 *    Navigation screenKey: 'inbox-def456' (generated once)
 *    Route cache key: '/inbox#inbox-def456'
 *    InboxPage mounts, Feed cached with display: none
 *
 * 3. Click Feed in TopBar (PUSH):
 *    REUSES existing screen key: 'feed-abc123' (singleton!)
 *    Route cache key: '/#feed-abc123' (same!)
 *    FeedPage restored from cache - NO REFRESH
 *
 * SCENARIO B: Detail Navigation (Feed → User → Videos - persistCache: false)
 * ---------------------------------------------------------------------------
 * 1. Feed → User Details (PUSH):
 *    Navigation screenKey: 'user-details-a3f9k2x7' (new)
 *    Route cache key: '/users/123#user-details-a3f9k2x7'
 *    UserDetailsPage mounts, fetches data
 *    Feed cached with display: none (persistCache: true)
 *
 * 2. User Details → Videos (PUSH):
 *    Navigation screenKey: 'user-videos-b8k3m1p2' (new)
 *    Route cache key: '/users/123/videos#user-videos-b8k3m1p2'
 *    UserVideosPage mounts, fetches data
 *    UserDetails cached with display: none
 *
 * 3. Videos → User Details (POP/back button):
 *    Cache removal:
 *    - Delete from cache state: '/users/123/videos#user-videos-b8k3m1p2'
 *    - Delete from cacheRef.current → React element UNMOUNTED from DOM
 *    - UserVideosPage onDispose fires → React Query cache cleaned
 *
 *    UserDetailsPage restored:
 *    - Screen key: 'user-details-a3f9k2x7' (unchanged)
 *    - Scroll position restored
 *    - onPopNext lifecycle fires
 *
 * 4. User Details → Feed (POP/back button):
 *    Cache removal:
 *    - Delete from cache state: '/users/123#user-details-a3f9k2x7'
 *    - Delete from cacheRef.current → React element UNMOUNTED from DOM
 *    - UserDetailsPage onDispose fires → React Query cache cleaned
 *
 *    FeedPage restored:
 *    - Screen key: 'feed-abc123' (unchanged, persistCache: true)
 *    - Scroll position restored
 *    - onPopNext lifecycle fires
 *
 * 5. Feed → User Details again (PUSH):
 *    Navigation screenKey: 'user-details-xyz999' (NEW! Cache was removed on POP)
 *    Route cache key: '/users/123#user-details-xyz999' (NEW!)
 *    NEW UserDetailsPage instance mounts
 *    Fresh data fetched (new component instance)
 *
 * SCREEN KEY GENERATION LOGIC:
 * =============================
 *
 * PUSH Navigation:
 * ---------------
 * Persistent routes (persistCache: true):
 * - First visit: Generate new screen key, cache forever
 * - Subsequent PUSH: Reuse existing screen key from cache (singleton)
 * - Example: Feed always uses same screen key, never refreshes
 *
 * Detail routes (persistCache: false):
 * - Re-render of same route: Reuse exact cache key
 * - New navigation: Generate NEW screen key
 * - Example: Each PUSH to UserDetails gets new instance
 *
 * POP Navigation (back/forward):
 * -----------------------------
 * - Search navigation stack for LAST matching cached route
 * - Restore screen key from cache
 * - If not in cache: Generate new screen key (first visit/direct access)
 *
 * CACHE CLEANUP STRATEGY:
 * =======================
 *
 * Route Cache Cleanup (RouteStack):
 * ---------------------------------
 * On POP navigation for routes with persistCache: false:
 * 1. Remove from cache state (removeFromCache)
 * 2. Delete from cacheRef.current → React element UNMOUNTED from DOM
 * 3. Clean up generated screen key from generatedScreenKeysRef
 * 4. Remove from syncedCacheKeysRef tracking
 *
 * Routes with persistCache: true:
 * - Never removed from cache
 * - Screen key reused forever
 * - Single instance per route
 *
 * React Query Cache Cleanup (Page Components):
 * --------------------------------------------
 * - Triggered in onDispose lifecycle when component unmounts
 * - queryClient.removeQueries({ predicate: (query) => query.queryKey[0] === queryScreenKey })
 * - Ensures no memory leaks
 *
 * SYNCHRONIZATION:
 * ================
 * cacheRef.current (local ref) ↔ cache (context state)
 * - Bidirectional sync effect ensures consistency
 * - syncedCacheKeysRef prevents infinite loops
 * - Immediate DOM removal on POP (don't wait for sync effect)
 *
 * BENEFITS OF THIS APPROACH:
 * ===========================
 * ✅ No circular dependencies between screen key generation and cache state
 * ✅ No infinite render loops
 * ✅ Persistent routes maintain singleton instances (Feed, Inbox, More)
 * ✅ Detail routes get fresh instances on each PUSH
 * ✅ Immediate React element cleanup on POP (not just display: none)
 * ✅ Proper lifecycle events (onPush, onPopNext, onPop, onDispose)
 * ✅ Each component instance has unique query/video keys
 * ✅ Multiple instances of same pathname can coexist correctly
 * ✅ Simple and predictable behavior
 */

interface RouteCache {
  [cacheKey: string]: {
    element: React.ReactElement;
    scrollPosition: number;
    lastVisited: number;
    screenKey?: string; // Store screenKey for debugging and cleanup
  };
}

interface KeepAliveContextType {
  cache: RouteCache;
  addToCache: (cacheKey: string, element: React.ReactElement, scrollPosition?: number, screenKey?: string) => void;
  removeFromCache: (cacheKey: string) => void;
  updateScrollPosition: (cacheKey: string, scrollPosition: number) => void;
  navigationStack: string[];
  updateNavigationStack: (cacheKey: string, action: 'PUSH' | 'POP' | 'REPLACE') => void;
  currentScreenKeyRef: React.MutableRefObject<string | undefined>;
}

const KeepAliveContext = createContext<KeepAliveContextType | undefined>(undefined);
/**
 * Provider component that manages the route cache with navigation stack tracking
 * Uses composite cache keys (pathname + screenKey) for fine-grained cache control
 * 
 * ADAPTIVE PERFORMANCE OPTIMIZATION:
 * ==================================
 * Monitors frame rendering performance to detect genuine performance issues vs. power saving modes.
 * 
 * When to reduce cache:
 * - ✅ Genuine lag: High frame times with user interaction (scrolling, clicking)
 * - ❌ Power saving: Low FPS but no recent user activity
 * - ❌ Background tab: Browser throttles requestAnimationFrame
 * - ❌ Low battery mode: Device intentionally reduces performance
 * 
 * Strategy:
 * 1. Track user interaction timestamps
 * 2. Only evict cache if lag occurs within 5 seconds of user activity
 * 3. Ignore performance issues when tab is hidden or inactive
 * 4. Use frame time (not just FPS) to detect actual rendering bottlenecks
 */
export const KeepAliveProvider: React.FC<{
  children: React.ReactNode;
  maxSize?: number;
  enableAdaptivePerformance?: boolean;
}> = ({
  children,
  maxSize = 10,
  enableAdaptivePerformance = false,
}) => {
    // const queryClient = useQueryClient();
    const [cache, setCache] = useState<RouteCache>({});
    const [navigationStack, setNavigationStack] = useState<string[]>([]);
    const currentScreenKeyRef = useRef<string | undefined>(undefined);
    const [adaptiveMaxSize, setAdaptiveMaxSize] = useState(maxSize);
    const lastUserInteractionRef = useRef(Date.now());
    const isDocumentVisibleRef = useRef(!document.hidden);

    // Tab-visibility tracking via the shared `useTabVisibility` hook.
    // The hook owns the listener lifecycle; the `enableAdaptivePerformance`
    // gate is enforced inside the callback (rather than via a hook arg)
    // because the hook's API is intentionally minimal — see the JSDoc on
    // `useTabVisibility` for the rationale.
    useTabVisibility((isVisible) => {
      if (!enableAdaptivePerformance) return;
      isDocumentVisibleRef.current = isVisible;
    });

    // Track user interactions to distinguish genuine lag from power saving
    useEffect(() => {
      if (!enableAdaptivePerformance) return;

      const updateInteractionTime = () => {
        lastUserInteractionRef.current = Date.now();
      };

      // Track meaningful user interactions
      window.addEventListener('scroll', updateInteractionTime, { passive: true });
      window.addEventListener('click', updateInteractionTime);
      window.addEventListener('touchstart', updateInteractionTime, { passive: true });
      window.addEventListener('keydown', updateInteractionTime);

      return () => {
        window.removeEventListener('scroll', updateInteractionTime);
        window.removeEventListener('click', updateInteractionTime);
        window.removeEventListener('touchstart', updateInteractionTime);
        window.removeEventListener('keydown', updateInteractionTime);
      };
    }, [enableAdaptivePerformance]);

    // Monitor frame rendering performance
    // CRITICAL: Memoize onLagStateChange to prevent infinite re-renders
    // The callback is in useFrameTimeMonitor's dependency array, so it must be stable
    const handleLagStateChange = useCallback((lagging: boolean) => {
      if (!enableAdaptivePerformance) return;

      // Only respond to lag if:
      // 1. Document is visible (not background tab)
      // 2. User interacted recently (within 5 seconds)
      // 3. Multiple consecutive lag frames (not transient spike)
      const timeSinceInteraction = Date.now() - lastUserInteractionRef.current;
      const isRecentInteraction = timeSinceInteraction < 5000; // 5 seconds
      const isVisible = isDocumentVisibleRef.current;

      if (lagging && isVisible && isRecentInteraction) {
        setCache(prevCache => {
          const cacheSize = Object.keys(prevCache).length;

          if (cacheSize > 3) {
            // Aggressively reduce cache to 3 entries
            console.warn('⚠️ [RouteStack] Performance lag detected - reducing cache from', cacheSize, 'to 3');
            setAdaptiveMaxSize(3);

            // Immediately evict oldest entries
            const entries = Object.entries(prevCache);
            const sorted = entries.sort((a, b) => a[1].lastVisited - b[1].lastVisited);
            const toRemove = sorted.slice(0, cacheSize - 3);

            const newCache = { ...prevCache };
            toRemove.forEach(([key]) => {
              delete newCache[key];
              // console.log('🗑️ [RouteStack] Evicted cache entry:', key);
            });
            return newCache;
          }
          return prevCache;
        });
      } else if (!lagging && isVisible) {
        // Performance recovered - gradually restore cache limit
        setAdaptiveMaxSize(prev => {
          if (prev < maxSize) {
            const newSize = Math.min(prev + 1, maxSize);
            // console.log('✅ [RouteStack] Performance recovered - increasing cache to', newSize);
            return newSize;
          }
          return prev;
        });
      }
    }, [enableAdaptivePerformance, maxSize]);

    useFrameTimeMonitor({
      enabled: enableAdaptivePerformance,
      lagThreshold: 50, // >50ms per frame = struggling
      smoothThreshold: 20, // <20ms per frame = smooth
      onLagStateChange: handleLagStateChange
    });

    const addToCache = useCallback((cacheKey: string, element: React.ReactElement, scrollPosition: number = 0, screenKey?: string) => {
      setCache(prevCache => {
        const newCache = { ...prevCache };
        newCache[cacheKey] = {
          element,
          scrollPosition,
          lastVisited: Date.now(),
          screenKey,
        };

        // Limit cache size using adaptive limit (may be reduced during lag)
        const entries = Object.entries(newCache);
        const effectiveMaxSize = enableAdaptivePerformance ? adaptiveMaxSize : maxSize;

        if (entries.length > effectiveMaxSize) {
          const sorted = entries.sort((a, b) => a[1].lastVisited - b[1].lastVisited);
          const toRemove = entries.length - effectiveMaxSize;
          for (let i = 0; i < toRemove; i++) {
            const [key] = sorted[i];
            delete newCache[key];
          }
        }

        return newCache;
      });
    }, [maxSize, adaptiveMaxSize, enableAdaptivePerformance]);

    const removeFromCache = useCallback((cacheKey: string) => {
      setCache(prevCache => {
        const newCache = { ...prevCache };
        delete newCache[cacheKey];
        return newCache;
      });
    }, []);

    const updateScrollPosition = useCallback((cacheKey: string, scrollPosition: number) => {
      setCache(prevCache => {
        if (prevCache[cacheKey]) {
          return {
            ...prevCache,
            [cacheKey]: {
              ...prevCache[cacheKey],
              scrollPosition,
              lastVisited: Date.now(),
            },
          };
        }
        return prevCache;
      });
    }, []);

    const updateNavigationStack = useCallback((cacheKey: string, action: 'PUSH' | 'POP' | 'REPLACE') => {
      setNavigationStack(prevStack => {
        const newStack = [...prevStack];

        if (action === 'PUSH') {
          // Forward navigation - add to stack
          newStack.push(cacheKey);
        } else if (action === 'POP') {
          // Back navigation - remove from stack
          if (newStack.length > 0) {
            newStack.pop();
          }
        } else if (action === 'REPLACE') {
          // Replace navigation - replace last item
          if (newStack.length > 0) {
            newStack[newStack.length - 1] = cacheKey;
          } else {
            newStack.push(cacheKey);
          }
        }

        return newStack;
      });
    }, []);

    // CRITICAL: Memoize context value to prevent unnecessary re-renders
    // Without this, every state change creates a new object reference,
    // causing all consumers (KeepAliveOutlet) to re-render infinitely
    const contextValue = useMemo(
      () => ({ cache, addToCache, updateScrollPosition, removeFromCache, navigationStack, updateNavigationStack, currentScreenKeyRef }),
      [cache, addToCache, updateScrollPosition, removeFromCache, navigationStack, updateNavigationStack, currentScreenKeyRef]
    );

    return (
      <KeepAliveContext.Provider value={contextValue}>
        {children}
      </KeepAliveContext.Provider>
    );
  };

/**
 * Check if a route should persist in cache (never be removed)
 * Main navigation routes are always cached
 *
 * @param pathname - Current pathname to check
 * @returns true if this route should persist in cache
 */
const shouldPersistCache = (pathname: string): boolean => {
  return MAIN_NAVIGATION_ROUTES.some(route => {
    // Handle root path specially since it already has a leading '/'
    if (route.path === '/') {
      return pathname === '/' && route.persistCache === true;
    }

    // For other paths, add leading '/' and convert params to regex pattern
    const pattern = route.path.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^/${pattern}$`);
    return regex.test(pathname) && route.persistCache === true;
  });
};

/**
 * Component that wraps the Outlet to keep routes alive
 * Caches routes by composite key (pathname + screenKey) and preserves scroll position
 */
export const KeepAliveOutlet: React.FC = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const outlet = useOutlet();
  const context = useContext(KeepAliveContext);
  const cacheRef = useRef<{ [cacheKey: string]: React.ReactElement }>({});
  const prevCacheKeyRef = useRef<string>('');
  const isInitialMount = useRef(true);
  const currentScrollRef = useRef<number>(0);
  const syncedCacheKeysRef = useRef<Set<string>>(new Set());
  // Store generated screen keys per pathname to prevent regeneration on subsequent renders
  const generatedScreenKeysRef = useRef<Map<string, string>>(new Map());

  if (!context) {
    throw new Error('KeepAliveOutlet must be used within KeepAliveProvider');
  }

  const { cache, addToCache, updateScrollPosition, removeFromCache, navigationStack, updateNavigationStack, currentScreenKeyRef } = context;

  // Calculate current cache key from pathname + screenKey
  const currentPathname = location.pathname;

  // Extract screen key from location.state (extract once to avoid dependency on entire object)
  const locationScreenKey = extractScreenKey(location.state);

  // CRITICAL: Generate or retrieve screen key using ONLY refs (no useMemo)
  // React doesn't guarantee useMemo will preserve values, so we use refs for true stability
  // This function runs on EVERY render but checks refs first to avoid regeneration
  // ALL ROUTES now use screen keys for consistent caching behavior
  let screenKey = locationScreenKey;

  // If no screen key provided from location state, determine it using refs
  if (!screenKey) {
    // PUSH navigation: Generate NEW screen key for fresh instance
    // Only reuse if this is a re-render of the current route (same pathname as previous)
    if (navigationType === 'PUSH') {
      // CRITICAL: Check if this route should persist in cache (e.g., Feed, Inbox, More)
      // These routes should ALWAYS reuse their existing screen key, never generate a new one
      if (shouldPersistCache(currentPathname)) {
        // Find existing cache entry for this persistent route
        const existingCacheEntry = Object.entries(cacheRef.current).find(([cacheKey]) => {
          const { pathname } = parseCacheKey(cacheKey);
          return pathname === currentPathname;
        });

        if (existingCacheEntry) {
          // Reuse existing screen key from cache (persistent routes are singletons)
          const [existingCacheKey] = existingCacheEntry;
          const { screenKey: cachedScreenKey } = parseCacheKey(existingCacheKey);
          screenKey = cachedScreenKey;

          // if (process.env.NODE_ENV !== 'production') {
          //   console.log('♻️ [RouteStack] Reusing screen key for persistent route (PUSH):', {
          //     pathname: currentPathname,
          //     screenKey,
          //     cacheKey: existingCacheKey
          //   });
          // }
        } else {
          // First time visiting this persistent route - generate screen key
          const prefix = getScreenKeyPrefix(currentPathname);
          screenKey = generateScreenKey(prefix);

          // if (process.env.NODE_ENV !== 'production') {
          //   console.log('🆕 [RouteStack] Generated screen key for persistent route (first visit):', {
          //     pathname: currentPathname,
          //     screenKey
          //   });
          // }
        }
      } else {
        // Non-persistent route - check if re-render or new navigation
        const prevPathname = prevCacheKeyRef.current ? parseCacheKey(prevCacheKeyRef.current).pathname : '';
        const isRerender = prevPathname === currentPathname && prevCacheKeyRef.current !== '';

        if (isRerender) {
          // Re-render of current route - reuse the EXACT SAME cache entry
          // CRITICAL: Only reuse if the exact cache key exists, not just any entry with same pathname
          const exactCacheKey = prevCacheKeyRef.current;
          if (cacheRef.current[exactCacheKey]) {
            const { screenKey: cachedScreenKey } = parseCacheKey(exactCacheKey);
            screenKey = cachedScreenKey;

            // if (process.env.NODE_ENV !== 'production') {
            //   console.log('🔄 [RouteStack] Reusing screen key for PUSH re-render:', {
            //     pathname: currentPathname,
            //     screenKey,
            //     cacheKey: exactCacheKey
            //   });
            // }
          } else {
            // Cache entry was removed, treat as new navigation
            const prefix = getScreenKeyPrefix(currentPathname);
            screenKey = generateScreenKey(prefix);

            // if (process.env.NODE_ENV !== 'production') {
            //   console.log('🆕 [RouteStack] Cache entry removed, generating new key for PUSH:', {
            //     pathname: currentPathname,
            //     screenKey,
            //     prevCacheKey: exactCacheKey
            //   });
            // }
          }
        } else {
          // New PUSH navigation - always generate NEW screen key
          const prefix = getScreenKeyPrefix(currentPathname);
          screenKey = generateScreenKey(prefix);

          // if (process.env.NODE_ENV !== 'production') {
          //   console.log('🆕 [RouteStack] Generated NEW screen key for PUSH:', {
          //     pathname: currentPathname,
          //     screenKey,
          //     prevPathname,
          //     isNewNavigation: true
          //   });
          // }
        }
      }
    }
    // POP navigation (back/forward/direct access): Try to reuse or generate
    else if (navigationType === 'POP') {
      // CRITICAL FIX: Use navigation stack to find the correct route to restore
      // When we have multiple instances of the same pathname (e.g., UserDetails #1 and #2),
      // searching by pathname returns the FIRST match, which is wrong!
      // Instead, we should look at the navigation stack to know which instance to restore.

      // Strategy: Find the LAST (most recent) route in the navigation stack that:
      // 1. Matches the current pathname
      // 2. Still exists in cache
      // 3. Is NOT the route we're currently leaving (to handle re-renders during same navigation)
      let cachedCacheKey: string | undefined = undefined;

      if (navigationStack.length > 0) {
        // Find all routes in stack that match current pathname and still exist in cache
        const matchingRoutes = navigationStack.filter(cacheKey => {
          if (!cacheRef.current[cacheKey]) return false;
          const { pathname } = parseCacheKey(cacheKey);
          return pathname === currentPathname;
        });

        // Use the LAST matching route (most recently pushed)
        // This correctly handles multiple instances of the same pathname
        if (matchingRoutes.length > 0) {
          cachedCacheKey = matchingRoutes[matchingRoutes.length - 1];
        }
      }

      // Fallback: If no match in stack, search cache by pathname (for direct browser access)
      if (!cachedCacheKey) {
        const cachedEntry = Object.entries(cacheRef.current).find(([cacheKey]) => {
          const { pathname } = parseCacheKey(cacheKey);
          return pathname === currentPathname;
        });

        if (cachedEntry) {
          cachedCacheKey = cachedEntry[0];
        }
      }

      if (cachedCacheKey) {
        // Found cached route - restore its screen key
        const { screenKey: cachedScreenKey } = parseCacheKey(cachedCacheKey);
        screenKey = cachedScreenKey;

        // if (process.env.NODE_ENV !== 'production') {
        //   console.log('♻️ [RouteStack] Restoring cached screen key for POP:', {
        //     pathname: currentPathname,
        //     screenKey,
        //     cacheKey: cachedCacheKey,
        //     fromStack: navigationStack.includes(cachedCacheKey),
        //     stackLength: navigationStack.length
        //   });
        // }
      } else {
        // Not in cache - check generatedScreenKeysRef (for direct access/refresh to prevent double-mount)
        const existingGeneratedKey = generatedScreenKeysRef.current.get(currentPathname);
        if (existingGeneratedKey) {
          screenKey = existingGeneratedKey;
          // if (process.env.NODE_ENV !== 'production') {
          //   console.log('♻️ [RouteStack] Reusing generated screen key for POP:', {
          //     pathname: currentPathname,
          //     screenKey
          //   });
          // }
        }

        // If still no key (first direct access), generate new one
        if (!screenKey) {
          const prefix = getScreenKeyPrefix(currentPathname);
          screenKey = generateScreenKey(prefix);

          // Store in ref to prevent regeneration on subsequent renders of same component
          generatedScreenKeysRef.current.set(currentPathname, screenKey);

          // if (process.env.NODE_ENV !== 'production') {
          //   console.log('🆕 [RouteStack] Generated new screen key for POP (direct access):', {
          //     pathname: currentPathname,
          //     screenKey
          //   });
          // }
        }
      }
    }
  }

  const currentCacheKey = composeCacheKey(currentPathname, screenKey);

  // CRITICAL: Set screen key in ref IMMEDIATELY during render (synchronous, no re-render)
  // Refs update synchronously, so children will see the correct value on first render
  if (currentScreenKeyRef.current !== screenKey) {
    // if (process.env.NODE_ENV !== 'production') {
    //   console.log('🔄 [RouteStack] Setting screen key in ref:', {
    //     oldKey: currentScreenKeyRef.current,
    //     newKey: screenKey,
    //     pathname: currentPathname,
    //     timestamp: new Date().toISOString()
    //   });
    // }
    currentScreenKeyRef.current = screenKey;
  }

  // Store outlet in local ref during render (safe)
  // CRITICAL FIX: Wrap outlet with a key prop to force new React instance per screen key
  // Without this, navigating to same pathname with different screen key reuses the same outlet
  if (outlet && !cacheRef.current[currentCacheKey]) {
    // if (process.env.NODE_ENV !== 'production') {
    //   console.log('✅ Caching NEW route:', currentCacheKey, '| scroll will be: 0');
    // }
    // Wrap the outlet in a fragment with a key to force React to create new instance
    // This prevents the wrong component instance from displaying when pathname is the same
    const wrappedOutlet = <React.Fragment key={screenKey}>{outlet}</React.Fragment>;
    cacheRef.current[currentCacheKey] = wrappedOutlet;
  } else if (!outlet) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('⚠️ No outlet for cache key:', currentCacheKey);
    }
  }

  // SYNC: Keep cacheRef in sync with context cache (bidirectional)
  useEffect(() => {
    // STEP 1: Clear syncedCacheKeysRef for any keys that are now in cache state
    // This ensures that keys synced to cache can be cleaned up if later removed
    // Fixes race condition with performance-based cache eviction
    syncedCacheKeysRef.current.forEach(key => {
      if (cache[key]) {
        syncedCacheKeysRef.current.delete(key);
        // if (process.env.NODE_ENV !== 'production') {
        //   console.log('✅ [RouteStack] Cleared synced tracking for successfully cached key:', key);
        // }
      }
    });

    // STEP 2: Sync TO context: If we have it in ref but not in context, add it
    // CRITICAL: Track synced keys to prevent infinite loop from state updates
    if (outlet && cacheRef.current[currentCacheKey] && !cache[currentCacheKey] && !syncedCacheKeysRef.current.has(currentCacheKey)) {
      // if (process.env.NODE_ENV !== 'production') {
      //   console.log('🔄 Syncing TO context cache:', currentCacheKey);
      // }

      // Mark as synced BEFORE calling addToCache to prevent re-triggering
      syncedCacheKeysRef.current.add(currentCacheKey);

      // Use the wrapped outlet from cacheRef to maintain consistency
      addToCache(currentCacheKey, cacheRef.current[currentCacheKey], 0, screenKey);
      return; // Early return to prevent removal during same effect
    }

    // STEP 3: Sync FROM context: Remove from ref if removed from context (e.g., on POP cleanup, performance eviction)
    // Only run this if we're not adding to cache in this effect
    Object.keys(cacheRef.current).forEach(key => {
      const { pathname } = parseCacheKey(key);

      // Don't remove if:
      // 1. It exists in cache (obviously)
      // 2. We just synced it to cache (state update might not be complete yet - race condition)
      //    Note: This should rarely happen now that we clear syncedCacheKeysRef in STEP 1
      // 3. This route should persist in cache (main navigation routes)
      if (!cache[key] && !syncedCacheKeysRef.current.has(key) && !shouldPersistCache(pathname)) {
        // if (process.env.NODE_ENV !== 'production') {
        //   console.log('🔄 Syncing FROM context cache - removing:', key);
        // }

        // Clean up generated screen key for this pathname ONLY if we're not currently on that pathname
        // This prevents infinite loops when direct-accessing a route (navigationType=POP)
        const { screenKey: removedScreenKey } = parseCacheKey(key);
        const storedKey = generatedScreenKeysRef.current.get(pathname);

        // Only delete if:
        // 1. The stored key matches the removed key (it's OUR generated key)
        // 2. We're NOT currently on this pathname (to prevent regeneration loop)
        if (storedKey === removedScreenKey && pathname !== currentPathname) {
          generatedScreenKeysRef.current.delete(pathname);
          // if (process.env.NODE_ENV !== 'production') {
          //   console.log('🗑️ [RouteStack] Cleaned up generated screen key:', { pathname, screenKey: removedScreenKey });
          // }
        }

        delete cacheRef.current[key];
        // Also remove from synced tracking
        syncedCacheKeysRef.current.delete(key);
      }
    });
  }, [currentCacheKey, outlet, cache, screenKey]); // Removed addToCache from dependencies

  // ==================== SCROLL POSITION MANAGEMENT ====================
  // This is the SINGLE SOURCE OF TRUTH for scroll restoration across all routes
  // It works automatically for ALL routes, regardless of whether they use useBasePage
  // 
  // How it works:
  // 1. Track window scroll continuously via event listener (currentScrollRef)
  // 2. Save scroll position when leaving a route (useLayoutEffect below)
  // 3. Restore scroll position when returning to cached route (useEffect below)
  // 
  // Note: We use window.scrollY (document-level scroll), not element.scrollTop
  // because the app scrolls the entire document, not a container element.
  // ====================================================================

  // Track scroll position continuously
  useEffect(() => {
    const handleScroll = () => {
      // Use window.scrollY for document-level scroll (not element scroll)
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      currentScrollRef.current = scrollTop;
      // if (process.env.NODE_ENV !== 'production') {
      //   console.log('📍 Scroll updated for:', currentCacheKey, '| scroll:', currentScrollRef.current);
      // }
    };

    // Capture initial scroll position when effect mounts
    currentScrollRef.current = window.scrollY || document.documentElement.scrollTop;

    // if (process.env.NODE_ENV !== 'production') {
    //   console.log('📍 Initial scroll captured for:', currentCacheKey, '| scroll:', currentScrollRef.current);
    // }

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [currentCacheKey]);

  // Save scroll position SYNCHRONOUSLY when pathname is about to change
  // useLayoutEffect runs before browser paint, ensuring we capture scroll before DOM updates
  useLayoutEffect(() => {
    const cacheKeyNow = currentCacheKey;
    const prevCacheKey = prevCacheKeyRef.current;

    if (cacheKeyNow === prevCacheKey) {
      return; // No change
    }

    // Save scroll position of the route we're LEAVING using the continuously tracked value
    if (prevCacheKey) {
      const scrollTop = currentScrollRef.current;
      // if (process.env.NODE_ENV !== 'production') {
      //   const shortKey = prevCacheKey.split('#')[1] ?? prevCacheKey;
      //   console.log(`🔵 [RouteStack] SAVE scroll | route: ${shortKey} | value: ${scrollTop} | action: ${navigationType}`);
      // }
      updateScrollPosition(prevCacheKey, scrollTop);
      // Reset for next route
      currentScrollRef.current = 0;
    }

    // Update navigation stack
    // CRITICAL: On POP navigation (back/forward/direct access), check if current route is already in stack
    // If not (direct access/refresh), add it. This ensures the stack always includes the current route.
    if (navigationType === 'POP') {
      // Check if current cache key is already in the navigation stack
      if (!navigationStack.includes(cacheKeyNow)) {
        // Direct access or refresh - add this route to the stack
        // if (process.env.NODE_ENV !== 'production') {
        //   console.log('📍 [RouteStack] Direct access/refresh - adding route to navigation stack:', cacheKeyNow);
        // }
        updateNavigationStack(cacheKeyNow, 'PUSH');
      } else {
        // Normal back navigation - pop the previous route
        updateNavigationStack(cacheKeyNow, 'POP');
      }
    } else {
      // PUSH or REPLACE navigation
      updateNavigationStack(cacheKeyNow, navigationType);
    }

    // ==================== CACHE CLEANUP ON POP ====================
    // When user presses back (POP), we remove BOTH caches:
    // 1. RouteStack cache (React component tree)
    // 2. React Query cache (API response data)
    //
    // WHY: Ensures fresh data on next PUSH navigation while preserving
    // cache when popping back TO this route.
    //
    // EXAMPLE:
    // - Navigate to User 123 (screenKey: abc)
    //   * Route cache: /users/123#abc
    //   * React Query: ['abc', 'userDetails', '123'], ['abc', 'userMedias', '123']
    //
    // - Navigate to Videos (screenKey: xyz) 
    //   * Route cache: /users/123/videos#xyz
    //   * React Query: ['xyz', 'videoList', '123']
    //
    // - Press back (POP)
    //   * Remove route cache: /users/123/videos#xyz ✓
    //   * Remove React Query: All queries with screenKey 'xyz' ✓
    //   * Keep User 123 caches (screenKey 'abc') ✓
    //
    // - Navigate to User 123 again (PUSH) → New screenKey (def) → Fresh data ✓
    // ==============================================================

    // CRITICAL: Remove cache on POP to prevent stale forward navigation
    // This ensures:
    // - POP back TO this route: Uses existing cache (we're returning to prevCacheKey)
    // - PUSH forward TO this route again later: Fresh data (we remove current cache when leaving via POP)
    //
    // IMPORTANT: Only remove caches for routes with persistCache: false
    // Main navigation routes (Feed, Inbox, More) have persistCache: true and stay cached forever
    if (navigationType === 'POP' && prevCacheKey) {
      const { pathname: prevPathname, screenKey: prevScreenKey } = parseCacheKey(prevCacheKey);

      // Only remove if previous route should NOT persist in cache
      // Main navigation routes (persistCache: true) stay cached forever
      if (!shouldPersistCache(prevPathname)) {
        // if (process.env.NODE_ENV !== 'production') {
        //   const scrollBeforeRemoval = cache[prevCacheKey]?.scrollPosition;
        //   console.log('🗑️ Removing cache on POP:', prevCacheKey, '| had scroll:', scrollBeforeRemoval);
        // }

        // Remove route cache (component tree) from both cache state AND cacheRef
        // CRITICAL: Remove from cacheRef immediately to unmount React element from DOM
        // Don't wait for sync effect - it has race conditions with syncedCacheKeysRef
        removeFromCache(prevCacheKey);

        // Remove from cacheRef to immediately unmount the React element
        if (cacheRef.current[prevCacheKey]) {
          delete cacheRef.current[prevCacheKey];
          // if (process.env.NODE_ENV !== 'production') {
          //   console.log('🗑️ Removed React element from DOM:', prevCacheKey);
          // }
        }

        // Clean up generated screen key for this pathname
        const storedKey = generatedScreenKeysRef.current.get(prevPathname);
        if (storedKey === prevScreenKey && prevPathname !== currentPathname) {
          generatedScreenKeysRef.current.delete(prevPathname);
          // if (process.env.NODE_ENV !== 'production') {
          //   console.log('🗑️ Cleaned up generated screen key:', { pathname: prevPathname, screenKey: prevScreenKey });
          // }
        }

        // Remove from synced tracking to prevent sync effect from trying to re-add
        syncedCacheKeysRef.current.delete(prevCacheKey);

      } else if (process.env.NODE_ENV !== 'production') {
        // console.log('⏩ Skipping cache removal for persistent route:', prevCacheKey);
      }
    }

    // Note: Caching is now done during render phase (before this effect runs)
    // to prevent mount/dispose/remount cycles

    prevCacheKeyRef.current = cacheKeyNow;

    // Mark as initialized after first render
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }

  }, [currentCacheKey, navigationType, updateScrollPosition, updateNavigationStack, removeFromCache]);

  // Restore scroll position after render
  useEffect(() => {
    const cachedRoute = cache[currentCacheKey];
    // const shortKey = currentCacheKey.split('#')[1] ?? currentCacheKey;
    if (cachedRoute && cachedRoute.scrollPosition !== undefined) {
      const targetScroll = cachedRoute.scrollPosition;
      // if (process.env.NODE_ENV !== 'production') {
      //   console.log(`🔵 [RouteStack] RESTORE queued | route: ${shortKey} | target: ${targetScroll} | window.scrollY now: ${window.scrollY}`);
      // }

      // Re-apply the saved scroll across a short settle window rather than a
      // single RAF. WHY: a kept-alive route is shown again by flipping its
      // wrapper from `display: none` to `display: block`. Routes whose content
      // settles its final height asynchronously after that flip — most notably
      // the chat page's reversed `VirtualList`, whose bottom-pin padding and
      // off-screen top-spacer grow over a few frames as item measurements land
      // (see `useReversedAnchor.ts`'s settle loop) — have a `document` scroll
      // height that is still under-estimated on the first frame. A single
      // `window.scrollTo(targetScroll)` then gets clamped to that stale,
      // smaller max-scroll and the page lands above where it was left (user
      // review fix plan 4, observation 2). Re-applying for a handful of frames
      // lets the restore "chase" the growing scroll height to the true target.
      //
      // We bail the moment the window actually reaches the target (so we never
      // fight a genuine user scroll, which only begins after this brief window)
      // and we re-issue only while the browser is still clamping us short.
      // Top-anchored routes reach the target on frame 0 and exit immediately,
      // so this is a no-op for every non-reversed page.
      const MAX_RESTORE_PASSES = 6;
      const SETTLE_TOLERANCE_PX = 2;
      let pass = 0;
      let rafId = 0;
      const applyRestore = () => {
        window.scrollTo(0, targetScroll);
        pass += 1;
        const reached =
          Math.abs((window.scrollY || 0) - targetScroll) <= SETTLE_TOLERANCE_PX;
        if (!reached && pass < MAX_RESTORE_PASSES) {
          rafId = requestAnimationFrame(applyRestore);
        }
      };
      rafId = requestAnimationFrame(applyRestore);
      return () => {
        if (rafId) cancelAnimationFrame(rafId);
      };
    } else {
      // if (process.env.NODE_ENV !== 'production') {
      //   console.log(`🔵 [RouteStack] RESET to 0 | route: ${shortKey} | cachedRoute exists: ${!!cachedRoute} | window.scrollY now: ${window.scrollY}`);
      // }
      // No saved position (genuine first mount / fresh push). Reset to the top
      // in a single call. We do NOT run the settle loop here: a reversed list's
      // genuine first mount intentionally bottom-anchors itself (after two RAFs
      // + its own settle loop, `useReversedAnchor.ts`), and a multi-frame
      // reset-to-0 would race that snap. Letting the anchor own the bottom on a
      // fresh chat open is the correct, conflict-free behavior.
      window.scrollTo(0, 0);
    }
  }, [currentCacheKey, cache]);

  // Log cached routes stack before rendering (development only)
  // This reflects what's actually in the DOM, synced with cacheRef
  // if (process.env.NODE_ENV !== 'production') {
  //   const cachedKeys = Object.keys(cacheRef.current);
  //   const stackDisplay = cachedKeys.map(key => {
  //     const { screenKey } = parseCacheKey(key);
  //     const isActive = key === currentCacheKey;
  //     return `${screenKey || 'no-key'}${isActive ? ' [ACTIVE]' : ''}`;
  //   }).join(' → ');

  //   console.log(`📚 [RouteStack] Navigation Stack: ${stackDisplay || '(empty)'}`);
  // }

  // Render all cached routes
  return (
    <>
      {Object.entries(cacheRef.current).map(([cacheKey, element]) => {
        const isActive = cacheKey === currentCacheKey;
        return (
          <div
            key={cacheKey}
            data-route-active={isActive} // Use to detect where to insert the ExpauseSheet
            style={{
              display: isActive ? 'block' : 'none',
              height: '100%',
              width: '100%',
            }}
          >
            {element}
          </div>
        );
      })}
    </>
  );
};

/**
 * Hook to access KeepAlive context
 * Provides access to navigation cache key and screen key
 */
export const useKeepAlive = () => {
  const context = useContext(KeepAliveContext);
  if (!context) {
    throw new Error('useKeepAlive must be used within KeepAliveProvider');
  }
  return context;
};
